import { ref, onMounted } from 'vue';
import { useAppStore } from '@store/app';

const useE2eMock = import.meta.env.VITE_ENV === 'e2e' && !import.meta.env.VITE_GOOGLE_MAP_API_KEY;

export default function useGoogleAutocompleteService() {
    const app = useAppStore();
    const autoCompleteService = ref(null);
    const error = ref(null);
    const hasError = ref(false);
    const loading = ref(false);
    const fetching = ref(false);
    const autoCompleteSessionToken = ref(null);

    async function initGoogle() {
        if (useE2eMock) {
            return;
        }

        try {
            loading.value = true;

            // grab pre-initiated app level autocomplete service
            autoCompleteService.value = app.autoCompleteService;

            // group requests together using a token to bill per session not per request
            // we need a new token for each group of searches, so this is not on app level
            autoCompleteSessionToken.value = new app.google.maps.places.AutocompleteSessionToken();

            error.value = null;
            hasError.value = false;
        } catch (e) {
            console.error('Failed to initialize Google Places API', e);
            error.value = e;
        } finally {
            loading.value = false;
        }
    }

    async function fetchResults(searchText, callback, options = {}) {
        if (useE2eMock) {
            fetching.value = true;
            callback([
                {
                    place_id: 'e2e_mock_place',
                    description: `${searchText}, QLD, Australia`,
                    types: ['street_address'],
                    structured_formatting: {
                        main_text: searchText,
                        main_text_matched_substrings: [{ offset: 0, length: searchText.length }],
                        secondary_text: 'QLD, Australia',
                    },
                },
            ]);
            fetching.value = false;
            return;
        }

        fetching.value = true;

        if (!autoCompleteService.value) {
            await initGoogle();
        }

        options = {
            input: searchText,
            componentRestrictions: { country: ['au'] },
            sessionToken: autoCompleteSessionToken.value,
            ...options,
        };

        autoCompleteService.value.getPlacePredictions(options, (predictions) => {
            callback(predictions);
            fetching.value = false;
        });
    }

    onMounted(() => {
        initGoogle();
    });

    return {
        autoCompleteService,
        fetchResults,
        initGoogle,
        error,
        hasError,
        loading,
        fetching,
    };
}
